// apps/web/src/features/kronos/timer/TimerEditPopover.tsx
import { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  Popover,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import type { TrackerProjectOption, TrackerDesignationOption } from '../TimeTrackerForm';
import { encodeExistingOptionId } from '../designationOptions';
import type { AutoStopPreset } from './timerMachine';
import { colors } from '../../../core/theme/theme';

interface TimerEditPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  projects: TrackerProjectOption[];
  designations: TrackerDesignationOption[];
  designationsLoading: boolean;
  currentProjectId: string;
  currentDesignationId: string;
  currentAutoStopAt: string | null;
  onProjectIdChange: (id: string) => void;
  onSave: (params: {
    projectId: string;
    designationId: string;
    autoStopPreset: AutoStopPreset;
    customAutoStopAt?: string;
  }) => void;
}

const PRESETS: { key: AutoStopPreset; labelKey: string }[] = [
  { key: 'none', labelKey: 'kronos.timer.autoStop.none' },
  { key: 'lunch', labelKey: 'kronos.timer.autoStop.lunch' },
  { key: 'end-of-day', labelKey: 'kronos.timer.autoStop.endOfDay' },
  { key: 'in-1h', labelKey: 'kronos.timer.autoStop.inHours' },
  { key: 'in-2h', labelKey: 'kronos.timer.autoStop.inHours' },
  { key: 'in-3h', labelKey: 'kronos.timer.autoStop.inHours' },
  { key: 'in-4h', labelKey: 'kronos.timer.autoStop.inHours' },
];

function presetCount(key: AutoStopPreset): number | undefined {
  const match = key.match(/^in-(\d+)h$/);
  return match ? Number(match[1]) : undefined;
}

export default function TimerEditPopover({
  anchorEl,
  open,
  onClose,
  projects,
  designations,
  designationsLoading,
  currentProjectId,
  currentDesignationId,
  currentAutoStopAt,
  onProjectIdChange,
  onSave,
}: TimerEditPopoverProps) {
  const { t } = useTranslation();
  const [projectId, setProjectId] = useState(currentProjectId);
  const [designationId, setDesignationId] = useState(currentDesignationId);
  const [autoStop, setAutoStop] = useState<AutoStopPreset>(currentAutoStopAt ? 'custom' : 'none');

  // Sync local state when popover opens with new values
  useEffect(() => {
    if (open) {
      setProjectId(currentProjectId);
      setDesignationId(currentDesignationId);
      setAutoStop(currentAutoStopAt ? 'custom' : 'none');
      // Ensure parent has the right designation subscription
      onProjectIdChange(currentProjectId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleProjectChange = (id: string) => {
    setProjectId(id);
    setDesignationId('');
    onProjectIdChange(id);
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <Box sx={{ p: 3, width: 360 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          {t('kronos.timer.editTimer')}
        </Typography>
        <Stack spacing={2}>
          <Autocomplete
            options={projects}
            value={projects.find((p) => p.id === projectId) ?? null}
            onChange={(_, v) => handleProjectChange(v?.id ?? '')}
            getOptionLabel={(opt) => `${opt.code} — ${opt.name}`}
            isOptionEqualToValue={(opt, v) => opt.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label={t('kronos.tracker.project')} size="small" />
            )}
          />
          <Autocomplete
            disabled={!projectId || designationsLoading}
            options={designations}
            value={designations.find((d) => d.id === encodeExistingOptionId(designationId)) ?? null}
            onChange={(_, v) => {
              // Store the raw designation ID (strip existing: prefix)
              const raw = v?.id ?? '';
              setDesignationId(raw.startsWith('existing:') ? raw.slice('existing:'.length) : raw);
            }}
            getOptionLabel={(opt) => opt.label}
            groupBy={(opt) => opt.groupLabel}
            isOptionEqualToValue={(opt, v) => opt.id === v.id}
            renderInput={(params) => (
              <TextField {...params} label={t('kronos.tracker.designation')} size="small" />
            )}
          />
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
              {t('kronos.timer.autoStop.label')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
              {PRESETS.map(({ key, labelKey }) => {
                const count = presetCount(key);
                const label = count !== undefined ? t(labelKey, { count }) : t(labelKey);
                return (
                  <Chip
                    key={key}
                    label={label}
                    size="small"
                    onClick={() => setAutoStop(key)}
                    variant={autoStop === key ? 'filled' : 'outlined'}
                    sx={{
                      ...(autoStop === key && {
                        bgcolor: colors.yellow,
                        color: '#000',
                      }),
                    }}
                  />
                );
              })}
            </Box>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            <Button size="small" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button
              size="small"
              variant="contained"
              color="primary"
              disabled={!projectId || !designationId}
              onClick={() => {
                onSave({ projectId, designationId, autoStopPreset: autoStop });
                onClose();
              }}
            >
              {t('common.save')}
            </Button>
          </Box>
        </Stack>
      </Box>
    </Popover>
  );
}
